import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  HStack,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  Button,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdDownload, MdUploadFile } from 'react-icons/md';
import { Task, TaskType } from '@/lib/sdk/chatbot/types';
import { GridRow, GridAction, taskGridReducer, blankRow, gridRowToTask, taskToGridRow } from './useTaskGridReducer';
import { validateGridRows } from './taskGridValidation';
import { ChatbotTaskImportApi } from '@/lib/sdk/chatbot/taskImportApi';
import { importRowToGridRow } from './taskGridImport';
import { SortableTaskRow } from './SortableTaskRow';
import { ALL_TYPE_OPTIONS } from './taskGridConstants';

// ─── Fill state ───────────────────────────────────────────────────────────────

interface FillState {
  srcRowIdx: number;
  col: string;
  value: string;
  srcType: TaskType;
  endRow: number;
}

// ─── WorkflowTaskGrid ─────────────────────────────────────────────────────────

export interface WorkflowTaskGridHandle {
  dispatchAdvanced: (localId: string, config: Partial<GridRow>) => void;
  getRows: () => GridRow[];
}

export interface WorkflowTaskGridProps {
  template: string;
  chatbotLifecycle: string;
  initialTasks?: Task[];
  onValidRows: (tasks: Task[]) => void;
  onValidationError: (count: number) => void;
  validateTriggerId?: string;
  onOpenConfigPanel?: (row: GridRow, rowIdx: number) => void;
  onOpenSubtaskEditor?: (row: GridRow, rowIdx: number) => void;
  onRowsChange?: (rows: GridRow[]) => void;
  /** Ids of tasks the AI assistant changed, highlighted until the page is left */
  highlightTaskIds?: ReadonlySet<string>;
  /** Extra toolbar buttons, after Export */
  toolbarActions?: ReactNode;
}

export const WorkflowTaskGrid = forwardRef<WorkflowTaskGridHandle, WorkflowTaskGridProps>(function WorkflowTaskGrid({
  template,
  chatbotLifecycle: _chatbotLifecycle,
  initialTasks,
  highlightTaskIds,
  onValidRows,
  onValidationError,
  validateTriggerId = 'workflow-task-grid-validate',
  onOpenConfigPanel,
  onOpenSubtaskEditor,
  onRowsChange,
  toolbarActions,
}: WorkflowTaskGridProps, ref) {
  // Uses the shared taskToGridRow (not a hand-rolled mapping here) so this initial
  // load and every other Task->GridRow conversion in the app stay a single source of
  // truth — a duplicate mapping is exactly how extractionConfig went missing here
  // before: taskToGridRow carried it, this literal didn't, and the config an author
  // had just saved silently failed to reappear on reopen.
  const [rows, dispatch] = useReducer(
    taskGridReducer,
    undefined,
    () => initialTasks && initialTasks.length > 0
      ? initialTasks.map(taskToGridRow)
      : [blankRow(), blankRow(), blankRow()]
  );

  const [addCount, setAddCount] = useState(5);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importErrors, setImportErrors] = useState<{ row: number; msg: string }[]>([]);
  const importFileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const [focusedCell, setFocusedCell] = useState<{ rowIdx: number; col: string } | null>(null);
  const [fillState, setFillState] = useState<FillState | null>(null);

  // Expose dispatch and row accessor for parent (config panel saves)
  const dispatchRef = useRef<React.Dispatch<GridAction>>(dispatch);
  dispatchRef.current = dispatch;
  const rowsRef = useRef<GridRow[]>(rows);
  rowsRef.current = rows;

  useImperativeHandle(ref, () => ({
    dispatchAdvanced: (localId: string, config: Partial<GridRow>) => {
      dispatchRef.current({ type: 'SET_ADVANCED', localId, config });
    },
    getRows: () => rowsRef.current,
  }), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      dispatch({ type: 'REORDER_ROWS', activeId: String(active.id), overId: String(over.id) });
    }
  }, []);

  const typeOptions = ALL_TYPE_OPTIONS;
  const fixedCols = useMemo(() => ['name', 'description', 'type', 'data', 'group'], []);

  // Focus management
  useEffect(() => {
    if (!focusedCell) return;
    const el = document.getElementById(`wt-cell-${focusedCell.rowIdx}-${focusedCell.col}`);
    if (!el) return;
    el.focus();
  }, [focusedCell]);

  // Notify parent whenever rows change (used for localStorage persistence)
  useEffect(() => {
    onRowsChange?.(rows);
  }, [rows]);

  // Prevent text selection during fill drag
  useEffect(() => {
    document.body.style.userSelect = fillState ? 'none' : '';
    return () => { document.body.style.userSelect = ''; };
  }, [!!fillState]);

  // Commit fill on mouseup
  useEffect(() => {
    if (!fillState) return;
    const handleMouseUp = () => {
      const { srcRowIdx, col, value, srcType, endRow } = fillState;
      if (endRow !== srcRowIdx) {
        const lo = Math.min(srcRowIdx, endRow);
        const hi = Math.max(srcRowIdx, endRow);
        for (let i = lo; i <= hi; i++) {
          if (i === srcRowIdx) continue;
          // For data column, only fill if target row has same type
          if (col === 'data' && rows[i]?.type !== srcType) continue;
          dispatch({
            type: 'SET_CELL',
            localId: rows[i]._localId,
            col: col as 'name' | 'description' | 'type' | 'data',
            value,
          });
        }
      }
      setFillState(null);
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [fillState, rows]);

  const startFill = useCallback((rowIdx: number, col: string, value: string, srcType: TaskType) => {
    setFillState({ srcRowIdx: rowIdx, col, value, srcType, endRow: rowIdx });
  }, []);

  const updateFillEndRow = useCallback((rowIdx: number) => {
    setFillState((prev) => (prev ? { ...prev, endRow: rowIdx } : null));
  }, []);

  const isFillTarget = useCallback((rowIdx: number, col: string): boolean => {
    if (!fillState || fillState.col !== col || rowIdx === fillState.srcRowIdx) return false;
    const lo = Math.min(fillState.srcRowIdx, fillState.endRow);
    const hi = Math.max(fillState.srcRowIdx, fillState.endRow);
    return rowIdx >= lo && rowIdx <= hi;
  }, [fillState]);

  const fillTargetShadow = 'inset 0 0 0 2px var(--chakra-colors-brand-500)';

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIdx: number, col: string) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const colIdx = fixedCols.indexOf(col);
      if (e.shiftKey) {
        if (colIdx > 0) {
          setFocusedCell({ rowIdx, col: fixedCols[colIdx - 1] });
        } else if (rowIdx > 0) {
          setFocusedCell({ rowIdx: rowIdx - 1, col: fixedCols[fixedCols.length - 1] });
        }
      } else {
        if (colIdx < fixedCols.length - 1) {
          setFocusedCell({ rowIdx, col: fixedCols[colIdx + 1] });
        } else if (rowIdx < rows.length - 1) {
          setFocusedCell({ rowIdx: rowIdx + 1, col: fixedCols[0] });
        } else {
          // At last cell of last row — add a row and move to it
          dispatch({ type: 'ADD_ROWS', count: 1 });
          setFocusedCell({ rowIdx: rowIdx + 1, col: fixedCols[0] });
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx < rows.length - 1) {
        setFocusedCell({ rowIdx: rowIdx + 1, col });
      }
    } else if (e.key === 'Escape') {
      setFocusedCell(null);
    }
  }, [fixedCols, rows.length]);

  // Validation exposed via hidden button
  const validate = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
    const filledRows = rows.filter((r) => r.name.trim() || r.description.trim() || r.data.trim());
    const errorMap = validateGridRows(filledRows);

    if (errorMap.size > 0) {
      for (const [localId, errors] of errorMap) {
        dispatch({ type: 'SET_ROW_ERRORS', localId, errors });
      }
      onValidationError(errorMap.size);
      return;
    }

    const tasks: Task[] = filledRows
      .filter((r) => r.name.trim())
      .map((r) => gridRowToTask(r));

    onValidRows(tasks);
  }, [rows, onValidRows, onValidationError]);

  useEffect(() => {
    const el = document.getElementById(validateTriggerId);
    if (!el) return;
    const handler = () => validate();
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [validate, validateTriggerId]);

  const filledCount = rows.filter((r) => r.name.trim()).length;
  const totalErrors = rows.reduce(
    (sum, r) => sum + Object.values(r._errors ?? {}).filter(Boolean).length,
    0
  );

  const showDataCol = true;

  const handleImport = async (file: File) => {
    setIsImporting(true);
    setImportErrors([]);
    try {
      const result = await ChatbotTaskImportApi.parseTasks(file);
      if (result.errors && result.errors.length > 0) {
        setImportErrors(result.errors.map((e) => ({ row: e.row_number ?? 0, msg: e.message ?? '' })));
        return;
      }
      if (!result.tasks || result.tasks.length === 0) {
        toast({ title: 'No tasks found in file', status: 'warning', duration: 3000 });
        return;
      }
      const gridRows = result.tasks.map(importRowToGridRow);
      dispatch({ type: 'APPEND_GRID_ROWS', rows: gridRows });
      toast({ title: `Imported ${result.tasks.length} tasks`, status: 'success', duration: 3000 });
    } catch {
      toast({ title: 'Import failed', description: 'Could not parse the file.', status: 'error', duration: 4000 });
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const tasks = rows
        .filter((r) => r.name.trim())
        .map((r) => ({
          task_name: r.name,
          description: r.description,
          type: r.type,
          data: r.data,
          group: r.group,
          mutable: r.mutable ?? false,
          mask_output: r.maskOutput ?? false,
        }));
      const blob = await ChatbotTaskImportApi.exportTasks(tasks);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chatbot_tasks.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Export failed', status: 'error', duration: 3000 });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await ChatbotTaskImportApi.getTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chatbot_tasks_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Failed to download template', status: 'error', duration: 3000 });
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <input
        ref={importFileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImport(file);
          e.target.value = '';
        }}
      />
      <HStack justify="space-between" mb={2} spacing={2} flexWrap="wrap">
        <HStack spacing={2}>
          <Tooltip label="Download Excel template">
            <Button
              size="sm"
              leftIcon={<MdDownload />}
              variant="outline"
              borderColor="border-default"
              data-testid="wfv4-grid-template-btn"
              onClick={handleDownloadTemplate}
            >
              Template
            </Button>
          </Tooltip>
          <Button
            size="sm"
            leftIcon={<MdUploadFile />}
            variant="outline"
            borderColor="border-default"
            isLoading={isImporting}
            loadingText="Importing..."
            data-testid="wfv4-grid-import-btn"
            onClick={() => importFileRef.current?.click()}
          >
            Import
          </Button>
          <Button
            size="sm"
            leftIcon={<MdDownload />}
            variant="outline"
            borderColor="border-default"
            isLoading={isExporting}
            data-testid="wfv4-grid-export-btn"
            onClick={handleExport}
          >
            Export
          </Button>
          {toolbarActions}
        </HStack>
        <HStack spacing={2}>
          <HStack spacing={1}>
            <Input
              type="number"
              value={addCount}
              onChange={(e) => setAddCount(Math.max(1, Math.min(100, Number(e.target.value))))}
              size="sm"
              w="60px"
              bg="bg-surface"
              borderColor="border-default"
              textAlign="center"
            />
            <Button
              size="sm"
              variant="outline"
              borderColor="border-default"
              data-testid="wfv4-grid-add-rows-btn"
              onClick={() => dispatch({ type: 'ADD_ROWS', count: addCount })}
            >
              Add {addCount} row{addCount !== 1 ? 's' : ''}
            </Button>
          </HStack>
          <Button
            size="sm"
            leftIcon={<MdAdd />}
            variant="outline"
            borderColor="border-default"
            data-testid="wfv4-grid-add-row-btn"
            onClick={() => dispatch({ type: 'ADD_ROWS', count: 1 })}
          >
            Add row
          </Button>
        </HStack>
      </HStack>

      {importErrors.length > 0 && (
        <Box mb={2} p={3} borderWidth="1px" borderColor="red.300" borderRadius="md" bg="red.50">
          <Text fontSize="sm" fontWeight="medium" color="red.700" mb={1}>Import errors:</Text>
          {importErrors.map((err, i) => (
            <Text key={i} fontSize="xs" color="red.600">Row {err.row}: {err.msg}</Text>
          ))}
        </Box>
      )}

      <Box
        overflowX="auto"
        data-testid="wfv4-grid-table"
        borderWidth="1px"
        borderColor="border-default"
        borderRadius="md"
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table
            variant="simple"
            size="sm"
            style={{ borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: showDataCol ? '940px' : '780px' }}
          >
            <Thead bg="bg-muted" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <Tr>
                <Th w="36px" px={1} color="text-secondary" borderColor="border-default" />
                <Th minW="200px" px={0} color="text-primary" borderColor="border-default">
                  Name <Text as="span" color="red.400">*</Text>
                </Th>
                <Th minW="240px" px={0} color="text-primary" borderColor="border-default">Description</Th>
                <Th minW="150px" px={0} color="text-primary" borderColor="border-default">
                  Type <Text as="span" color="red.400">*</Text>
                </Th>
                {showDataCol && (
                  <Th minW="160px" px={0} color="text-primary" borderColor="border-default">Data</Th>
                )}
                <Th minW="120px" px={0} color="text-primary" borderColor="border-default">Group</Th>
                <Th w="110px" px={1} color="text-primary" borderColor="border-default">Configure</Th>
                <Th w="68px" px={1} borderColor="border-default" />
              </Tr>
            </Thead>
            <SortableContext items={rows.map((r) => r._localId)} strategy={verticalListSortingStrategy}>
              <Tbody>
                {rows.map((row, rowIdx) => (
                  <SortableTaskRow
                    key={row._localId}
                    isHighlighted={!!row.id && !!highlightTaskIds?.has(row.id)}
                    row={row}
                    rowIdx={rowIdx}
                    dispatch={dispatch}
                    template={template}
                    showDataCol={showDataCol}
                    typeOptions={typeOptions}
                    isFillTarget={isFillTarget}
                    fillTargetShadow={fillTargetShadow}
                    fillState={fillState}
                    handleKeyDown={handleKeyDown}
                    startFill={startFill}
                    updateFillEndRow={updateFillEndRow}
                    onOpenConfigPanel={onOpenConfigPanel}
                    onOpenSubtaskEditor={onOpenSubtaskEditor}
                  />
                ))}
                {rows.length === 0 && (
                  <Tr>
                    <Td colSpan={showDataCol ? 8 : 7} textAlign="center" py={8} color="text-secondary">
                      No tasks — click "Add row" to get started.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </SortableContext>
          </Table>
        </DndContext>
      </Box>

      <VStack align="flex-start" spacing={0} mt={2}>
        {totalErrors > 0 && (
          <Text data-testid="wfv4-grid-error-count" fontSize="sm" color="red.500">
            {totalErrors} validation error{totalErrors !== 1 ? 's' : ''} — fix highlighted rows before saving.
          </Text>
        )}
        <Text fontSize="xs" color="text-secondary">
          Tab to move between cells · Enter to move down · Drag the blue corner handle to fill a value down
          {filledCount > 0 && ` · ${filledCount} task${filledCount !== 1 ? 's' : ''} ready`}
        </Text>
      </VStack>

      <button id={validateTriggerId} style={{ display: 'none' }} />
    </Box>
  );
});

export type { GridRow, GridAction };
